import React, { useCallback } from 'react';

import type { UUID } from '@aline/common';
import { gql } from '@apollo/client';
import { Typography } from '@mui/material';
import { AlineTooltip } from 'src/components';
import { InlineEditField } from 'src/components/generic/InlineEditInput';
import { Apollo } from 'src/gql/generated';
import { makeStyles } from 'tss-react/mui';

void gql`
  mutation UpdateTaskTitle($input: UpdateTaskInput!) {
    updateTask(input: $input) {
      task {
        __typename
        id
        title
      }
    }
  }
`;

const TITLE_LINE_HEIGHT = '30px';

const useStyles = makeStyles()(theme => ({
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  sectionLabel: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    fontWeight: 600,
  },
  titleDisplay: {
    color: theme.appColors.iconsType,
    fontSize: 24,
    fontWeight: 700,
    lineHeight: TITLE_LINE_HEIGHT,
  },
  titleInput: {
    fontSize: 24,
    fontWeight: 700,
    height: 'auto',
    lineHeight: TITLE_LINE_HEIGHT,
    padding: 0,
  },
  // The task page's header card: a larger title with no label above it.
  titlePage: {
    color: theme.appColors.strongType,
    fontSize: 28,
    lineHeight: '36px',
  },
}));

interface TaskTitleFieldProps {
  isReadOnly: boolean;
  taskId: UUID;
  title: string;
  editDisabledReason?: string | null;
  variant?: 'page' | 'panel';
}

export const TaskTitleField = React.memo(function TaskTitleField({
  editDisabledReason,
  isReadOnly,
  taskId,
  title,
  variant = 'panel',
}: TaskTitleFieldProps) {
  const { classes, cx } = useStyles();
  const isPage = variant === 'page';
  const [updateTaskTitle] = Apollo.useUpdateTaskTitleMutation();

  const handleTitleSave = useCallback(
    async (value: string) => {
      await updateTaskTitle({
        optimisticResponse: {
          __typename: 'Mutation',
          updateTask: {
            __typename: 'TaskResponse',
            task: { __typename: 'Task', id: taskId, title: value.trim() },
          },
        },
        variables: { input: { props: { title: value.trim() }, taskId } },
      });
    },
    [taskId, updateTaskTitle]
  );

  const displayClass = cx(classes.titleDisplay, isPage && classes.titlePage);

  return (
    <div className={classes.row}>
      {!isPage && <div className={classes.sectionLabel}>Title</div>}
      <TaskTitleValue
        displayClass={displayClass}
        editDisabledReason={editDisabledReason}
        inputClass={cx(classes.titleInput, isPage && classes.titlePage)}
        isReadOnly={isReadOnly}
        title={title}
        onSave={handleTitleSave}
      />
    </div>
  );
});

function TaskTitleValue({
  displayClass,
  editDisabledReason,
  inputClass,
  isReadOnly,
  onSave,
  title,
}: {
  displayClass: string;
  inputClass: string;
  isReadOnly: boolean;
  onSave: (value: string) => Promise<void>;
  title: string;
  editDisabledReason?: string | null;
}): JSX.Element {
  if (isReadOnly)
    return <Typography className={displayClass}>{title || 'Untitled task'}</Typography>;

  return (
    <AlineTooltip arrow placement="top" title={editDisabledReason ?? ''}>
      <div>
        <InlineEditField
          classes={{ display: displayClass, input: inputClass }}
          disabled={Boolean(editDisabledReason)}
          placeholder="Add a title..."
          textPlaceholder="Untitled Task"
          value={title}
          onSave={onSave}
        />
      </div>
    </AlineTooltip>
  );
}
